import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import moment from "moment";
import { MonthlyOrderModel } from "src/app/_models/monthly-order.model";
import { AdvancedReorderAddedComponent } from "./added/advanced-reorder-added.component";
import { Component, HostListener, inject } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { AdvancedReorderRuleModel } from "src/app/_models/advanced-reorder-rule.model";
import { LoadingService } from "src/app/_services/loading.service";
import { AdvancedReorderService } from "src/app/_services/advanced-reorder.service";
import { AdvancedReorderRulesService } from "src/app/_services/advanced-reorder-rules.service";
import { AdvancedReorderRules } from "src/app/_global/advanced-reorder-rules";
import { Suppliers } from "src/app/_global/suppliers";
import { AddedReorderModel } from "src/app/_models/added-reorder.model";
import { Locations } from "src/app/_global/locations";
import { StoresManager } from "src/app/_global/stores-manager";
import { RangePickerComponent } from "../theme/range-picker/range-picker.component";
import { AdvancedReorderItem, AdvancedReorderListingSetting, AdvancedReorderSetting,
  AdvancedReorderSubSetting } from "./advanced-reorder-item";
import { ProductOrders } from "./product-orders";
import { ItemOrders } from "./item-orders";
import { AdvancedReorderSliderComponent } from "./slider/advanced-reorder-slider.component";
import { AdvancedReorderConfig } from "./advanced-reorder-config";
import { AdvancedReorderSettingModel } from "src/app/_models/advanced-reorder-setting.model";
import { Observable } from "rxjs";
import { PendingChangesGuard } from "src/app/_services/pending-changes.guard";
import { HasSubSettingsComponent } from "./has-sub-settings/has-sub-settings.component";
import { ToastrService } from "ngx-toastr";

const ALL_DAYS = [30, 14, 7];
const MONTHS = ['-01-01','-02-01','-03-01','-04-01','-05-01','-06-01','-07-01','-08-01','-09-01','-10-01','-11-01','-12-01'];

@Component({ template: '' })
export class AdvancedReorderHelper implements PendingChangesGuard {
  popoverMessage: string;
  today = moment();
  showPacks: boolean = false;
  itemsToLoad: AdvancedReorderItem[] = [];
  loaded: number;
  ruleChanged: boolean = false;
  unsavedChanges: boolean = false;
  settingChanged: boolean = false;
  minusAllInventory: boolean;
  minusAllStoresInventory: boolean;
  minusAllStoresOverstockedInventory: boolean;
  formGroup: UntypedFormGroup;
  locationsFormGroup: UntypedFormGroup;
  storesFormGroup: UntypedFormGroup;
  overstockedStoresFormGroup: UntypedFormGroup;
  allMonths = MONTHS;
  selectedRule: AdvancedReorderRuleModel;
  addedReorders: AddedReorderModel[] = [];
  main: AdvancedReorderItem;
  allItems: AdvancedReorderItem[] = [];
  orderFor: RangePickerComponent;
  basedOn: RangePickerComponent;
  configs: AdvancedReorderConfig = new AdvancedReorderConfig();
  itemConfigs: AdvancedReorderConfig;
  activeSlider: AdvancedReorderSliderComponent;
  applyToAll: boolean = false;
  productId :number;
  subReorder :number;
  subIncluded :boolean;
  loadingService: LoadingService;
  toastr: ToastrService;
  modalService: NgbModal;
  advReorderService: AdvancedReorderService;
  advReorderRulesService: AdvancedReorderRulesService;
  advReorderRules: AdvancedReorderRules;
  locations: Locations;
  suppliers: Suppliers;
  stores: StoresManager;
  fb: UntypedFormBuilder;
  
  @HostListener('document:mouseup', ['$event']) mouseUp(e: MouseEvent) {
    if (this.activeSlider) {
      this.activeSlider.circleMouseUp(e);
      this.activeSlider = null;
    }
  }
  @HostListener('document:mousemove', ['$event']) mouseMove(e: MouseEvent) {
    if (this.activeSlider) {
      if (e.buttons == 0) {
        this.activeSlider.circleMouseUp(e);
        this.activeSlider = null;
      }
      else
        this.activeSlider.circleMouseMove(e);
    }
  }
  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    return !this.unsavedChanges;
  }
  
  constructor() {
    this.loadingService = inject(LoadingService);
    this.toastr = inject(ToastrService);
    this.modalService = inject(NgbModal);
    this.advReorderService = inject(AdvancedReorderService);
    this.advReorderRulesService = inject(AdvancedReorderRulesService);
    this.advReorderRules = inject(AdvancedReorderRules);
    this.locations = inject(Locations);
    this.suppliers = inject(Suppliers);
    this.stores = inject(StoresManager);
    this.fb = inject(UntypedFormBuilder);  
  }

  prepare(item: AdvancedReorderItem) {
    this.setItemForms(item);
    item.adjSign = item.data.adjustment ? item.data.adjustment < 0 ? '-' : '+' : item.adjSign;
    item.selected = [];
    item.totalOrderFor = 0;
    item.years = new Map<number, MonthlyOrderModel[]>();
    if (!item.data.basedOn && !item.data.basedOnMonths) {
      if (this.basedOn) {
        item.data.basedOn = this.basedOn.selectedDays;
        item.data.basedOnMonths = this.basedOn.hasCustom ? `{${this.basedOn.getSelectedMonths().join(',')}}` : null;
      } else {
        item.data.basedOn = this.configs.basedOnSelectedDays;
        item.data.basedOnMonths = this.configs.basedOnHasCustom ? `{${this.configs.basedOnSelectedMonths.map(m => `20${m[1]}-${m[0] < 9 ? '0' : ''}${m[0]+1}-01`).join(',')}}` : null;
      }
    }
    if (!item.data.orderFor && !item.data.orderForMonths) {
      if (this.orderFor) {
        item.data.orderFor = this.orderFor.selectedDays;
        item.data.orderForMonths = this.orderFor.hasCustom ? `{${this.orderFor.getSelectedMonths().join(',')}}` : null;
      } else {
        item.data.orderFor = this.configs.orderForSelectedDays;
        item.data.orderForMonths = this.configs.orderForHasCustom ? `{${this.configs.orderForSelectedMonths.map(m => `20${m[1]}-${m[0] < 9 ? '0' : ''}${m[0]+1}-01`).join(',')}}` : null;
      }
    }
    if (!item.parentId) {
      item.suppliers = [];
      if (item.data.allSupplierIds) {
        let allSupplierIds = item.data.allSupplierIds.split(';');
        for (let supplierId of allSupplierIds) {
          let supplier = this.suppliers.suppliers.find(s => s.id == Number(supplierId));
          if (supplier) {
            supplier.notes = `${item.data.sku}'s suppliers`;
            item.suppliers.push(supplier);
          }
        }
      }
      item.suppliers = item.suppliers.concat(this.suppliers.suppliers);
    }
    this.refreshOrders(item);
  }

  setItemForms(item: AdvancedReorderItem) {
    item.hasCustomSettings = !!(item.data.basedOn || item.data.basedOnMonths);
    item.minusAllInventory = item.minusAllInventory !== undefined ? item.minusAllInventory : item.data.minusInventory == 'true';
    item.minusAllStoresInventory = item.minusAllStoresInventory !== undefined ? item.minusAllStoresInventory : item.data.minusStoreInventory == 'true';
    item.formGroup = this.fb.group({
      supplierId: [item.data.supplierId, Validators.required],
      adjustment: item.data.adjustment !== undefined ? Math.round((item.data.adjustment * 100)) : (this.formGroup?.value.adjustment || this.configs.adjustment),
      minusInventory: item.minusAllInventory, minusStoreInventory: item.minusAllStoresInventory,
      minusOverstockedStoreInventory: item.minusAllStoresOverstockedInventory,
      minusPOs: item.data.minusOpenPurchases !== undefined ? item.data.minusOpenPurchases : (this.formGroup?.value.minusPOs || this.configs.minusPOs),
      outOfStock: item.data.outOfStock !== undefined ? item.data.outOfStock : (this.formGroup?.value.outOfStock || this.configs.outOfStock),
      buyBox: item.data.buyBox !== undefined ? item.data.buyBox : (this.formGroup?.value.buyBox || this.configs.buyBox), totalDays: 0,
      manualEntry: item.data.manualEntry,
      reorderRuleId: item.data.advancedReorderRuleId || (item.hasCustomSettings ? undefined : this.selectedRule?.id)
    });
    item.formGroup.controls.supplierId.valueChanges.subscribe(supplierId => {
      item.data.supplierId = supplierId;
      item.supplier = this.suppliers.suppliers.find(s => s.id == supplierId);
      if (item.supplier) {
        this.setDayToArrive(item, item.supplier.defaultBySea ? 'bySea' : 'byAir',
          item.supplier.defaultBySea ? item.supplier.bySea : item.supplier.byAir);
        if (item.orderForYears) {
          let totalDays = 0;
          item.orderForYears.forEach((months) => {
            for (let monthlyData of months)
              totalDays += monthlyData.days = this.getAvailableDays(item, monthlyData.date.toDateString());
          });
          item.formGroup.controls.totalDays.setValue(totalDays);
        }
        this.unsavedChanges = false;
      }
    });
    if (item.data.supplierId) {
      if(!this.main)
        item.formGroup.controls.supplierId.setValue(item.data.supplierId);
      else if (item == this.main)
        this.formGroup.controls.supplierId.setValue(item.data.supplierId);
    }
    item.formGroup.controls.adjustment.valueChanges.subscribe(value => this.refreshAdjustment(item, value));
    ['minusPOs', 'outOfStock', 'buyBox', 'totalDays', 'manualEntry'].forEach(ctrl =>
      item.formGroup.controls[ctrl].valueChanges.subscribe(() => setTimeout(() => this.refreshAdjustment(item), 1)));
    item.formGroup.controls.reorderRuleId.valueChanges.subscribe(value => this.refreshRule(item, value));
    item.formGroup.controls.minusInventory.valueChanges.subscribe(value => {
      this.setAllLocations(item, value);
      setTimeout(() => this.refreshAdjustment(item), 1);
    });
    item.formGroup.controls.minusStoreInventory.valueChanges.subscribe(value => {
      this.setAllStores(item, value);
      if (!value)
        item.formGroup.controls.minusOverstockedStoreInventory.setValue(false);
      setTimeout(() => this.refreshAdjustment(item), 1);
    });
    item.formGroup.controls.minusOverstockedStoreInventory.valueChanges.subscribe(value => {
      this.setAllOverstockedStores(item, value);
      setTimeout(() => this.refreshAdjustment(item), 1);
    });
    let locationsIds: number[] = [], storesIds: number[] = [], overstockedStoresIds: number[] = [];
    let locationsForm = {}, storesForm = {}, overstockedStoresForm = {};
    if (!item.minusAllInventory && item.data.minusInventory)
      locationsIds = item.data.minusInventory.split(',').map(id => Number(id));
    if (!item.minusAllStoresInventory && item.data.minusStoreInventory)
      storesIds = item.data.minusStoreInventory.split(',').map(id => Number(id));
    if (!item.minusAllStoresOverstockedInventory && item.data.minusOverstockedStoreInventory)
      overstockedStoresIds = item.data.minusOverstockedStoreInventory.split(',').map(id => Number(id));
    for (let location of this.locations.locations)
      locationsForm[location.id] = item.minusAllInventory ? true : locationsIds.includes(location.id);
    for (let store of this.stores.getAll()) {
      storesForm[store.id] = item.minusAllStoresInventory ? true : storesIds.includes(store.id);
      overstockedStoresForm[store.id] = item.minusAllStoresOverstockedInventory ? true : overstockedStoresIds.includes(store.id);
    }
    item.locationsFormGroup = this.fb.group(locationsForm);
    item.locationsFormGroup.valueChanges.subscribe(() => { this.selectLocation(item) });
    item.storesFormGroup = this.fb.group(storesForm);
    item.storesFormGroup.valueChanges.subscribe(() => { this.selectStore(item) });
    item.overstockedStoresFormGroup = this.fb.group(overstockedStoresForm);
    item.overstockedStoresFormGroup.valueChanges.subscribe(() => { this.selectOverstockedStore(item) });
    if (item.minusAllInventory)
      this.setAllLocations(item, true, true);
    else if (locationsIds.length)
      item.formGroup.controls.minusInventory.setValue(true, { emitEvent: false });
    if (item.minusAllStoresInventory)
      this.setAllStores(item, true, true);
    else if (storesIds.length)
      item.formGroup.controls.minusStoreInventory.setValue(true, { emitEvent: false });
    if (item.minusAllStoresOverstockedInventory)
      this.setAllOverstockedStores(item, true, true);
    else if (overstockedStoresIds.length)
      item.formGroup.controls.minusOverstockedStoreInventory.setValue(true, { emitEvent: false });
    setTimeout(() => {
      if (item.data.advancedReorderRuleId)
        item.formGroup.controls.reorderRuleId.setValue(item.data.advancedReorderRuleId, {emitEvent: false});
      else
        setTimeout(() => item.ruleChanged = false, 100);
    }, 0);
  }

  refreshOrders(item: AdvancedReorderItem) {
    let reorder = this.addedReorders.find(r => r.productId == (item.parentId || item.data.productId));
    if (reorder) {
      if (reorder.addedQuantity) {
        if (item.parentId && reorder.details) {
          if (this.main != item) {
            let sub = reorder.details?.subs.find(s => s.productId == item.data.productId);
            if (sub) {
              if (sub.quantity)
                item.data.addedQuantity = sub.quantity;
              if (sub.manualEntry)
                item.data.manualEntry = sub.manualEntry;
              if (sub.adjustment)
                item.data.adjustment = sub.adjustment;
            }
            else
              item.data.addedQuantity = 0;
          } else {
            if (reorder.details.manualEntry)
              item.data.manualEntry = reorder.details.manualEntry;
            if (reorder.details.adjustment)
              item.data.adjustment = reorder.details.adjustment;
          }
          if (item.data.manualEntry) {
            if (item.data.manualEntry < 0)
              item.meSign = '-';
            item.formGroup.controls.manualEntry.setValue(Math.abs(item.data.manualEntry));
          }
          if (item.data.adjustment) {
            if (item.data.adjustment < 0)
              item.adjSign = '-';
            item.formGroup.controls.adjustment.setValue(Math.abs(item.data.adjustment));
          }
        }
      } else {
        this.addedReorders.splice(this.addedReorders.indexOf(reorder), 1);
        delete item.data.addedQuantity;
      }
    } else
      delete item.data.addedQuantity;
  }

  refreshAdjustment(item: AdvancedReorderItem, value: any = item.formGroup.value.adjustment) {
    if (item.ruleChanged === false && item.formGroup.value.reorderRuleId && !this.loaded)
      item.formGroup.controls.reorderRuleId.setValue(null);
    item.data.adjustment = (item.adjSign == '-' ? -1 : 1) * ((value || 0) / 100);
    item.data.manualEntry = (item.meSign == '-' ? -1 : 1) * (item.formGroup.value.manualEntry || 0);
    if (this.main && !item.data.storeId) {
      let sp = this.main.data.storeProducts.find(sp => sp.productId == item.data.productId && !sp.storeId);
      if (sp) {
        sp.adjustment = item.data.adjustment;
        sp.manualEntry = item.data.manualEntry;
      }
    }
    this.setTotalReorder(item);
    if (!item.unsavedChanges) {
      item.unsavedChanges = true;
    }
    if (!this.unsavedChanges) {
      this.unsavedChanges = true;
    }
  }

  refreshRule(item: AdvancedReorderItem, newValue: any) {
    item.ruleChanged = true;
    if (newValue && newValue != item.formGroup.value.reorderRuleId && this.advReorderRules.rules) {
      let rule = this.advReorderRules.rules.find(r => r.id == newValue);
      if (rule) {
        item.formGroup.controls.outOfStock.setValue(rule.outOfStock, { emitEvent: false });
        item.formGroup.controls.buyBox.setValue(rule.buyBox, { emitEvent: false });
        item.formGroup.controls.minusPOs.setValue(rule.minusOpenPurchases, { emitEvent: false });
        item.formGroup.controls.adjustment.setValue(Math.abs(rule.adjustment), { emitEvent: false });
        item.adjSign = rule.adjustment < 0 ? '-' : '+';
        item.data.basedOn = rule.basedOn;
        item.data.basedOnMonths = rule.basedOnMonths;
        item.data.orderFor = rule.orderFor;
        item.data.orderForMonths = rule.orderForMonths;
        item.formGroup.controls.minusInventory.setValue(false);
        item.formGroup.controls.minusInventory.setValue(rule.minusInventory);
        item.formGroup.controls.minusStoreInventory.setValue(false);
        item.formGroup.controls.minusStoreInventory.setValue(rule.minusStoreInventory);
        item.formGroup.controls.minusOverstockedStoreInventory.setValue(false);
        item.formGroup.controls.minusOverstockedStoreInventory.setValue(rule.minusOverstockedStoreInventory);
        this.loadingService.loading = true;
        this.loadMonthlyData(item);
      }
    }
    setTimeout(() => item.ruleChanged = false, 100);
  }

  loadMonthlyData(item: AdvancedReorderItem) {
    item.years = new Map<number, MonthlyOrderModel[]>();
    item.orderForYears = new Map<number, MonthlyOrderModel[]>();
    let selectedMonths = item.data.basedOnMonths ? item.data.basedOnMonths.replace(/{|}/g, '').split(',') : [];
    if (item.data.basedOnMonths) {
      for (const month of selectedMonths)
        item.years.set(Number(month.substring(0, 4)), []);
    }
    else
      item.years.set(this.today.year(), []);
    let years: number[] = [];
    let allMonths: string[] = [];
    item.years.forEach((_, year: number) => {
      years.push(year);
      for (let m of MONTHS)
        allMonths.push(`${year}${m}`);
    });
    if (item.data.orderForMonths) {
      for (let month of item.data.orderForMonths.replace(/{|}/g, '').split(',')) {
        let year = Number(month.substring(0, 4));
        if (!item.orderForYears.has(year))
          item.orderForYears.set(year, []);
        let days = this.getAvailableDays(item, month);
        let monthData = new MonthlyOrderModel();
        monthData.date = new Date(`${month}T00:00:00Z`);
        monthData.days = days;
        monthData.selected = days > 0;
        item.orderForYears.get(year).push(monthData);
        item.totalOrderFor += monthData.days;
      }
      item.formGroup?.controls.totalDays.setValue(item.totalOrderFor, { emitEvent: false });
    }
    else {
      item.orderForYears.set(this.today.year(), []);
      item.formGroup?.controls.totalDays.setValue(item.data.orderFor, { emitEvent: false });
    }
    this.advReorderService.getMonthlyOrders(item.data.productId, item.data.storeId, years, true, !item.parentId && !item.data.storeId)
      .subscribe(res => {
        item.latest = [];
        for (const [i, days] of ALL_DAYS.entries()) {
          let data = res.latest.find(i => i.days == days);
          if (!data) {
            data = new MonthlyOrderModel();
            data.days = days;
          }
          item.latest.push(data);
          if (item.data.basedOn) {
            if (item.data.basedOn == days) {
              data.selected = true;
              item.selected.push(data);
            }
            if (i > 0 && item.data.basedOn == ALL_DAYS[i-1])
              data.leftSelected = true;
          }
        }
        for (let [i, month] of allMonths.entries()) {
          let date = new Date(`${month}T00:00:00Z`);
          let year = date.getUTCFullYear();
          let data = res.monthly.find(i => i.date.getTime() == date.getTime());
          if (!data) {
            data = new MonthlyOrderModel();
            data.date = date;
          }
          if (!item.years.has(year))
            item.years.set(year, []);
          item.years.get(year).push(data);
          data.days = moment(month).daysInMonth();
          if (item.data.basedOnMonths) {
            if (selectedMonths.includes(month)) {
              data.selected = true;
              item.selected.push(data);
            }
            else if (!month.endsWith('-01-01') && selectedMonths.includes(allMonths[i-1]))
              data.leftSelected = true;
          }
        }
        this.loaded -= 1;
        if (this.loaded <= 0)
          this.loadingService.loading = false;
        if (item == this.main)
          this.setTotalReorder();
        else {
          this.setTotalReorder(item);
          this.loadNext();
        }
      });
  }

  loadNext() {
    if (this.itemsToLoad.length) {
      let item = this.itemsToLoad.pop()
      this.loadMonthlyData(item);
    }
    else
      setTimeout(() => this.unsavedChanges = false, 0);
  }

  selectLocation(item: any) {
    let minusInventory = false;
    for (let locationId of Object.keys(item.locationsFormGroup.value))
      if (item.locationsFormGroup.value[locationId])
        minusInventory = true;
    if (item.formGroup.value.minusInventory != minusInventory)
      item.formGroup.controls.minusInventory.setValue(minusInventory, { emitEvent: false });
    if (item == this)
      this.ruleChanged = true;
    else
      this.refreshAdjustment(item);
  }

  selectStore(item: any) {
    let minusStoreInventory = false;
    for (let storeId of Object.keys(item.storesFormGroup.value))
      if (item.storesFormGroup.value[storeId])
        minusStoreInventory = true;
      else
        item.overstockedStoresFormGroup.controls[storeId].setValue(false);
    if (item.formGroup.value.minusStoreInventory != minusStoreInventory)
      item.formGroup.controls.minusStoreInventory.setValue(minusStoreInventory, { emitEvent: false });
    if (item == this)
      this.ruleChanged = true;
    else
      this.refreshAdjustment(item);
  }

  selectOverstockedStore(item: any) {
    let minusOverstockedStoreInventory = false;
    for (let storeId of Object.keys(item.overstockedStoresFormGroup.value))
      if (item.overstockedStoresFormGroup.value[storeId])
        minusOverstockedStoreInventory = true;
    if (item.formGroup.value.minusOverstockedStoreInventory != minusOverstockedStoreInventory)
      item.formGroup.controls.minusOverstockedStoreInventory.setValue(minusOverstockedStoreInventory, { emitEvent: false });
    if (item == this)
      this.ruleChanged = true;
    else
      this.refreshAdjustment(item);
  }

  setAllLocations(item: any, value: boolean, force: boolean = false) {
    item.minusAllInventory = value;
    if ((item.formGroup.value.minusInventory != value && item.locationsFormGroup) || force) {
      for (let locationId of Object.keys(item.locationsFormGroup.value)) {
        if (item.locationsFormGroup.value[locationId] != value)
          item.locationsFormGroup.controls[locationId].setValue(value, { emitEvent: false });
      }
    }
  }

  setAllStores(item: any, value: boolean, force: boolean = false) {
    item.minusAllStoresInventory = value;
    if ((item.formGroup.value.minusStoreInventory != value && item.storesFormGroup) || force) {
      for (let storeId of Object.keys(item.storesFormGroup.value)) {
        if (item.storesFormGroup.value[storeId] != value)
          item.storesFormGroup.controls[storeId].setValue(value, { emitEvent: false });
      }
    }
  }

  setAllOverstockedStores(item: any, value: boolean, force: boolean = false) {
    item.minusAllStoresOverstockedInventory = value;
    if ((item.formGroup.value.minusOverstockedStoreInventory != value && item.overstockedStoresFormGroup) || force) {
      for (let storeId of Object.keys(item.overstockedStoresFormGroup.value)) {
        if (item.overstockedStoresFormGroup.value[storeId] != value)
          item.overstockedStoresFormGroup.controls[storeId].setValue(value, { emitEvent: false });
      }
    }
  }

  setDayToArrive(item: AdvancedReorderItem, shipBy: string, days: number) {
    if (this.main)
    {
      this.main.shipBy = shipBy;
      for (let item of this.allItems)
        item.shipBy = shipBy;
      if (this.main.daysToArrive !== days) {
        this.main.daysToArrive = days;
        for (let itm of this.allItems)
        {
          itm.daysToArrive = days;
          if (itm.data.orderForMonths)
            this.selectOrderForMonth(itm, null);
        }
      }
    } else {
      item.shipBy = shipBy;
      if (item.daysToArrive !== days) {
        item.daysToArrive = days;
        if (item.data.orderForMonths)
          this.selectOrderForMonth(item, null);
      }
    }
  }

  disabledMonth(date: Date) {
    return new Date() < date;
  }

  selectMonth(item: AdvancedReorderItem, monthData: MonthlyOrderModel, element: any = null) {
    if (item.data.addedQuantity) {
      if (!this.loaded)
        this.modalService.open(AdvancedReorderAddedComponent);
      return;
    } else if (item.data.hasSubRule) {
      this.modalService.open(HasSubSettingsComponent).result.then(res => {
        if (res) {
          item.data.hasSubRule = false;
          this.selectMonth(item, monthData, element);
        }
      });
      return;
    }
    if (monthData.date) {
      let date = monthData.date.toISOString().split('T')[0];
      let daysData = item.selected.find(i => !i.date);
      if (moment() >= moment(monthData.date) && (!daysData || daysData.days < moment().diff(moment(monthData.date).add(1, 'month'), 'days'))) {
        let allMonths = item.data.basedOnMonths ? item.data.basedOnMonths.replace(/{|}/g, '').split(',').filter(m => m) : [];
        let ix = allMonths.indexOf(date);
        if (ix == -1)
        {
          allMonths.push(date);
          allMonths.sort();
          item.selected.push(monthData);
        }
        else if (item.selected.length > 1) {
          allMonths.splice(ix, 1);
          item.selected.splice(item.selected.indexOf(monthData), 1);
        }
        if (allMonths.length)
          item.data.basedOnMonths = `{${allMonths.join(',')}}`;
        else
          delete item.data.basedOnMonths;
        item.formGroup.controls.reorderRuleId.setValue(null);
        item.unsavedChanges = true;
        this.unsavedChanges = true;
      } else {
        if (daysData)
          this.popoverMessage = `Can not select this month as it is part of last ${daysData.days} days`;
        else
          this.popoverMessage = `Can not select this month as it is not started yet`;
        setTimeout(() => element.open(), 0);
      }
    }
    else {
      let latest = item.selected.filter(l => l.date).reduce((a, b) => a.date > b.date ? a : b, new MonthlyOrderModel());
      if (!latest.date || moment().diff(moment(latest.date).add(1, 'month'), 'days') >= monthData.days)
      {
        if (item.data.basedOn == monthData.days && item.selected.length > 1) {
          item.selected = item.selected.filter(i => i.date != null);
          delete item.data.basedOn
        }
        else if (item.data.basedOn != monthData.days) {
          item.data.basedOn = monthData.days;
          item.selected = item.selected.filter(i => i.date != null);
          item.selected.push(monthData);
        }
        item.formGroup.controls.reorderRuleId.setValue(null);
        item.unsavedChanges = true;
        this.unsavedChanges = true;
      } else {
        this.popoverMessage = `Can not select the last ${monthData.days} days as it is part of the selected months`;
        setTimeout(() => element.open(), 0);
      }
    }

    let selectedMonths: string[] = item.data.basedOnMonths ? item.data.basedOnMonths.replace(/{|}/g, '').split(',') : [];
    for (const [i, days] of ALL_DAYS.entries())
    {
      let data = item.latest.find(i => i.days == days);
      data.selected = false;
      data.leftSelected = false;
      if (item.data.basedOn)
      {
        if (item.data.basedOn == days)
          data.selected = true;
        if (i > 0 && item.data.basedOn == ALL_DAYS[i-1])
          data.leftSelected = true;
      }
    }
    item.years.forEach((months, year) => {
      months.forEach((data, i) => {
        data.selected = false;
        data.leftSelected = false;  
        if (item.data.basedOnMonths)
        {
          if (selectedMonths.includes(data.date.toISOString().split('T')[0]))
            data.selected = true;
          else if (!data.date.toString().includes("Jan 01") && selectedMonths
            .includes(item.years.get(year)[i-1]?.date?.toISOString().split('T')[0]))
            data.leftSelected = true;
        }        
      });
    });
    this.setTotalReorder(item);
  }

  selectOrderForMonth(item: AdvancedReorderItem, date: string, days: number = null) {
    if (moment(`${date}T00:00:00Z`).add(1, 'month') < this.today.clone().add(item.daysToArrive, 'days'))
      return;
    if (item.data.addedQuantity) {
      if (!this.loaded)
        this.modalService.open(AdvancedReorderAddedComponent);
      return;
    } else if (item.data.hasSubRule) {
      this.modalService.open(HasSubSettingsComponent).result.then(res => {
        if (res) {
          item.data.hasSubRule = false;
          this.selectOrderForMonth(item, date, days);
        }
      });
      return;
    }
    item.totalOrderFor = 0;
    if (date) {
      delete item.data.orderFor;
      let allMonths = item.data.orderForMonths ? item.data.orderForMonths.replace(/{|}/g, '').split(',') : [];
      let ix = allMonths.indexOf(date);
      if (ix == -1)
      {
        allMonths.push(date);
        allMonths.sort();
      }
      else if (ix > 0 || allMonths.length > 0)
        allMonths.splice(ix, 1);
      if (allMonths.length == 0) {
        delete item.data.orderForMonths;
        item.data.orderFor = item.totalOrderFor = 30;
        item.formGroup.controls.totalDays.setValue(30);
      }
      else {
        if (allMonths.length)
          item.data.orderForMonths = `{${allMonths.join(',')}}`;
        else
          delete item.data.orderForMonths;
      }
    }
    else if (days) {
      item.data.orderFor = days;
      delete item.data.orderForMonths;
    }
    var years = new Map<number, MonthlyOrderModel[]>();
    if (item.orderForYears) {
      item.orderForYears.forEach((_, year) => {
        years.set(year, []);
      });
    }
    item.orderForYears = years;
    if (item.data.orderForMonths)
    {
      for (const month of item.data.orderForMonths.replace(/{|}/g, '').split(','))
      {
        let year = Number(month.substring(0, 4));
        if (!item.orderForYears.has(year))
          item.orderForYears.set(year, []);
        let selected = new MonthlyOrderModel();
        selected.date = new Date(`${month}T00:00:00Z`);
        selected.selected = true;
        selected.days = this.getAvailableDays(item, month);
        item.orderForYears.get(year).push(selected);
        item.totalOrderFor += selected.days;
      }
      item.formGroup.controls.totalDays.setValue(item.totalOrderFor);
    }
    else
      item.orderForYears.set(this.today.year(), []);
  }

  getAllYears(years: Map<number, MonthlyOrderModel[]>, reversed: boolean = true) {
    if (years) {
      return reversed ? Array.from(years.keys()).reverse() : Array.from(years.keys());
    }
    else
      return [];
  }

  getAllBasedOnYears() {
    var years = [];
    for (let i = 0; i <= 5; i ++)
      years.push(this.today.year() - i);
    return years;
  }

  getAllOrderForYears() {
    var years = [];
    for (let i = 0; i < 3; i ++)
      years.push(this.today.year() + i);
    return years;
  }

  changeBasedOnYear(item: AdvancedReorderItem, oldYear: any, newYear: any) {
    this.addBasedOnYear(item, newYear, () => {
      for (var i = 0; i < 12; i++) {
        if (item.years.get(oldYear)[i].selected)
          this.selectMonth(item, item.years.get(newYear)[i]);
      }
      this.removeBasedOnYear(item, oldYear);
    });
  }

  changeOrderForYear(item: AdvancedReorderItem, oldYear: any, newYear: any) {
    item.orderForYears.set(newYear, item.orderForYears.get(oldYear));
    item.orderForYears.delete(oldYear);
    item.data.orderForMonths = item.data.orderForMonths?.replace(new RegExp(oldYear, 'g'), newYear);
    for (let monthlyOrders of item.orderForYears.get(newYear)) {
      monthlyOrders.date.setUTCFullYear(newYear);
      if (monthlyOrders.date.getUTCMonth() == 1)
        monthlyOrders.days = moment(monthlyOrders.date).daysInMonth();
    }
  }

  canAddYear(years: Map<number, MonthlyOrderModel[]>) {
    if (years)
      return years.keys().next().value != (this.today.year() - 5);
      //return Array.from(years.keys())[0] != (item.today.year() - 5);
    else
      return true;
  }

  addBasedOnYear(item: AdvancedReorderItem, newYear: number = null, callBack: Function = null) {
    if (item.data.addedQuantity) {
      if (!this.loaded)
        this.modalService.open(AdvancedReorderAddedComponent);
      return;
    } else if (item.data.hasSubRule) {
      this.modalService.open(HasSubSettingsComponent).result.then(res => {
        if (res) {
          item.data.hasSubRule = false;
          this.addBasedOnYear(item, newYear, callBack);
        }
      });
      return;
    }
    if (!newYear)
      newYear = Array.from(item.years.keys())[0] - 1;
    this.loadingService.loading = true;
    this.advReorderService.getMonthlyOrders(item.data.productId, item.data.storeId, [newYear],
      false, !item.parentId && !item.data.storeId)
      .subscribe(res => {
        item.years.set(newYear, []);
        for (let month of MONTHS) {
          let date = new Date(`${newYear}${month}T00:00:00Z`);
          let data = res.monthly.find(i => i.date.getTime() == date.getTime());
          if (!data) {
            data = new MonthlyOrderModel();
            data.date = date;
          }
          item.years.get(newYear).push(data);
          data.days = moment(date).daysInMonth();
        }
        if (callBack)
          callBack();
        this.loadingService.loading = false;
      });
  }

  removeBasedOnYear(item: AdvancedReorderItem, year: number) {
    if (item.data.addedQuantity) {
      if (!this.loaded)
        this.modalService.open(AdvancedReorderAddedComponent);
      return;
    } else if (item.data.hasSubRule) {
      this.modalService.open(HasSubSettingsComponent).result.then(res => {
        if (res) {
          item.data.hasSubRule = false;
          this.removeBasedOnYear(item, year);
        }
      });
      return;
    }
    for (let monthData of item.years.get(year)) {
      if (monthData.selected)
        this.selectMonth(item, monthData);
    }
    item.years.delete(year);
  }

  addOrderForYear(item: AdvancedReorderItem) {
    if (item.data.addedQuantity) {
      if (!this.loaded)
        this.modalService.open(AdvancedReorderAddedComponent);
      return;
    } else if (item.data.hasSubRule) {
      this.modalService.open(HasSubSettingsComponent).result.then(res => {
        if (res) {
          item.data.hasSubRule = false;
          this.addOrderForYear(item);
        }
      });
      return;
    }
    const newYear = Number(Array.from(item.orderForYears.keys()).reverse()[0]) + 1;
    item.orderForYears.set(newYear, []);
  }

  removeOrderForYear(item: AdvancedReorderItem, year: number) {
    if (item.data.addedQuantity) {
      if (!this.loaded)
        this.modalService.open(AdvancedReorderAddedComponent);
      return;
    } else if (item.data.hasSubRule) {
      this.modalService.open(HasSubSettingsComponent).result.then(res => {
        if (res) {
          item.data.hasSubRule = false;
          this.removeOrderForYear(item, year);
        }
      });
      return;
    }
    for (let monthlyOrders of item.orderForYears.get(year)) {
      if (monthlyOrders.selected)
        this.selectOrderForMonth(item, monthlyOrders.date.toISOString().split('T')[0]);
    }
    item.orderForYears.delete(year);
  }

  disableReorderMonth(item: AdvancedReorderItem, date: string) {
    let mDate = new Date(`${date}T00:00:00Z`);
    if (moment(date).endOf('month') > moment().add('days', item.daysToArrive) || item.orderForYears
      .get(mDate.getUTCFullYear())?.find(m => m.date?.getTime() == mDate.getTime())?.selected)
      return null;
    return true;
  }
  
  getMonthDays(date: string) {
    return moment(date).daysInMonth();
  }

  isOrderForMonthSelected(item: AdvancedReorderItem, date: string) {
    let mDate = new Date(`${date}T00:00:00Z`);
    return item.orderForYears.get(mDate.getUTCFullYear())?.find(m => m.date?.getTime() == mDate.getTime())?.selected
      && this.getAvailableDays(item, date) > 0;
  }

  getAvailableDays(item: AdvancedReorderItem, date: string) {
    let diff = moment(date).diff(moment().add('days', item.daysToArrive), 'days') - 1;
    diff = moment(date).daysInMonth() + (diff < 0 ? diff : 0);
    return diff > 0 ? diff : 0;
  }

  saveItemSettingsAsRule(item: AdvancedReorderItem, name: string) {
    this.loadingService.loading = true;
    let rule = new AdvancedReorderRuleModel();
    rule.name = name;
    rule.isDefault = false;
    rule.basedOn = item.data.basedOn;
    rule.basedOnMonths = item.data.basedOnMonths;
    rule.orderFor = item.data.orderFor;
    rule.orderForMonths = item.data.orderForMonths;
    rule.outOfStock = item.formGroup.value.outOfStock;
    rule.buyBox = item.formGroup.value.buyBox;
    rule.minusInventory = item.formGroup.value.minusInventory;
    rule.minusOpenPurchases = item.formGroup.value.minusPOs;
    rule.minusStoreInventory = item.formGroup.value.minusStoreInventory;
    rule.minusOverstockedStoreInventory = item.formGroup.value.minusOverstockedStoreInventory;
    rule.minusLocations = Object.keys(item.locationsFormGroup.value).map(id => Number(id))
      .filter(id => item.locationsFormGroup.value[id] == true);
    rule.minusStores = Object.keys(item.storesFormGroup.value).map(id => Number(id))
      .filter(id => item.storesFormGroup.value[id] == true);
    rule.minusOverstockedStores = Object.keys(item.overstockedStoresFormGroup.value).map(id => Number(id))
      .filter(id => item.overstockedStoresFormGroup.value[id] == true);
    rule.adjustment = item.formGroup.value.adjustment * (item.adjSign == '-' ? -1 : 1);
    this.advReorderRulesService.create(rule).subscribe((res) => {
      rule.id = res.id;
      this.advReorderRules.rules.push(rule);
      this.advReorderService.saveRuleSelection(res.id, item.data.productId, item.data.storeId, !!this.main)
        .subscribe(() => {
          item.formGroup.controls.reorderRuleId.setValue(res.id);
          this.toastr.success(`Default reorder rule has been set to SKU: ${item.data.sku}`);
          this.loadingService.loading = false;
          this.settingChanged = true;
        }, err => this.loadingService.loading = false);
    }, err => this.loadingService.loading = false);
    this.modalService.dismissAll();
  }

  setRuleAsItemDefault(item: AdvancedReorderItem) {
    this.loadingService.loading = true;
    this.advReorderService.saveRuleSelection(item.formGroup.value.reorderRuleId,
      item.data.productId, item.data.storeId, !!this.main)
      .subscribe(() => {
        item.data.advancedReorderRuleId = item.formGroup.value.reorderRuleId;
        this.toastr.success(`Default reorder rule has been set to SKU: ${item.data.sku}`);
        this.loadingService.loading = false;
      }, err => this.loadingService.loading = false);
  }

  open(content: any) {
    this.modalService.open(content);
  }

  isAddDisabled(item: AdvancedReorderItem) {
    return (item.data.addedQuantity || item.totalReorder) == 0 || !item.data.supplierId ? true : null;
  }

  showQuantity(item: AdvancedReorderItem, quantity: number) {
    return this.showPacks ? Math.round(quantity / (item.data.fraction || 1)) : quantity;
  }

  inventory(item: AdvancedReorderItem) {
    let inventory = 0;
    if (item.data.locations)
    {
      if (item.locationsFormGroup) {
        item.data.locations.forEach((inv, locationId) => {
          if (item.locationsFormGroup.value[locationId] || !item.formGroup.value.minusInventory)
            inventory += inv || 0;
        });
      }
    }
    return inventory;
  }

  realTime(item: AdvancedReorderItem) {
    let realTime = 0;
    if (item.data.storeProducts)
    {
      if (item.storesFormGroup) {
        for (const sp of item.data.storeProducts) {
          if (item.storesFormGroup.value[sp.storeId])
            realTime += sp.realTime || 0;
        }
      }
    }
    return realTime;
  }

  getRealtime(item: AdvancedReorderItem, storeId: number) {
    let realTime = 0;
    for (let listing of item.data.storeProducts?.filter(s => s.storeId == storeId))
      realTime += listing?.realTime || 0;
    return realTime;
  }

  totalInventory(item: AdvancedReorderItem) {
    let inventory = 0;
    item.data?.locations?.forEach(inv => inventory += inv || 0);
    return inventory;
  }

  getTotalRealtime(item: AdvancedReorderItem) {
    let realTime = 0;
    for (const listing of item.data.storeProducts)
      realTime += listing?.realTime || 0;
    return realTime;
  }

  sumOrders(data: MonthlyOrderModel, outOfStock: boolean = false, buyBox: boolean = false) {
    let totalOrders: number = 0;
    if (data.listings) {
      for (let listing of data.listings) {
        if (listing.orders > 0) {
          let percent: number = 1;
          if (outOfStock)
            percent = listing.inStock || 0;
          if (buyBox) {
            let buyBoxValue = listing.buyBox || 0;
            if (buyBoxValue < percent)
              percent = buyBoxValue;
          }
          totalOrders += listing.orders / (percent || 1);
        }
      }
    }
    return totalOrders;
  }

  inStockAvg(data: MonthlyOrderModel) {
    let inStock: number = 0, orders: number = 0;
    if (data.listings) {
      for (let listing of data.listings) {
        if (listing.orders > 0) {
          inStock += listing.orders/(listing.inStock || 1);
          orders += listing.orders;
        }
        else if (!inStock)
          inStock += listing.inStock;
      }
    }
    if (orders)
      return orders / inStock;
    else
      return inStock;
  }

  isOverstockedDisabled(item: AdvancedReorderItem) {
    return Object.keys(item.overstockedStoresFormGroup.value).some(storeId => item.overstockedStoresFormGroup.value[storeId])
      || Object.keys(item.storesFormGroup.value).every(storeId => item.storesFormGroup.value[storeId]) ? null : true;
  }

  last30DaysOrders(item: AdvancedReorderItem) {
    if (item.latest) {
      let last30Days = item.latest.find(l => l.days == 30);
      if (last30Days?.listings?.length)
        return last30Days.listings.reduce((a, b) => a + (b.orders || 0), 0);
    }
    return 1;
  }

  totalDays(item: AdvancedReorderItem) {
    return (item.selected || []).reduce((a, b) => a + (b.listings?.reduce((c, d) => c + (d.orders ? 1 : 0), 0) > 0 ? b.days : 0), 0);
  }

  addToOrder(item: AdvancedReorderItem) {
    let isNew = false;
    if (this.main && item !== this.main) {
      let order = this.addedReorders.find(r => r.productId == this.main.data.productId);
      if (!this.main.data.addedQuantity) {
        this.main.data.addedQuantity = 0;
        order = new AddedReorderModel();
        order.productId = this.main.data.productId;
        order.supplierId = this.formGroup.value.supplierId;
        order.addedQuantity = 0;
        order.details = new AdvancedReorderSetting();
        order.allPrices = [{ supplierId: this.formGroup.value.supplierId, price: this.main.data.pricePlus }];
        isNew = true;
      }
      if (order && order.details) {
        let sub = order.details.subs.find(s => s.productId == item.data.productId);
        if (sub) {
          order.details.subs = order.details.subs.filter(s => s.productId != item.data.productId);
          order.details.listings = order.details.listings.filter(l => l.productId != item.data.productId);
          order.addedQuantity = Math.ceil(order.addedQuantity - (sub.quantity * (1 + (order.details.adjustment || 0))));
        } else {
          let allOrders: ProductOrders[] = [];
          if (item?.selected)
            this.setAllOrders(item, allOrders, item.formGroup.value.outOfStock, item.formGroup.value.outOfStock.buyBox);
          let productOrders = 0;
          let productOrder = allOrders.find(po => po.productId == item.data.productId);
          if (productOrder) {
            let totalOrderFor: number = item.formGroup?.value?.totalDays || item.totalOrderFor;
            let days: number = (item.formGroup ? item.formGroup.value.totalDays : 0) || item.data.orderFor || item.totalOrderFor;
            for (let listing of productOrder.listings) {
              if (days !== undefined && totalOrderFor) {
                listing.quantity = (listing.quantity / this.totalDays(item)) * totalOrderFor;
                if (listing.quantity > 0 && (item.minusAllStoresInventory ||
                    item.storesFormGroup ? item.storesFormGroup.value[listing.storeId] : item.data.minusStores.includes(listing.storeId))) {
                  let realTime = item.data.storeProducts.find(sp => sp.storeId == listing.storeId && sp.productId == item.data.productId)?.realTime || 0;
                  if (realTime > listing.quantity)
                    listing.quantity = 0;
                  else
                    listing.quantity -= realTime;
                }
                listing.quantity = (listing.quantity / totalOrderFor) * days;
              }
              listing.quantity = listing.quantity;
              productOrders += listing.quantity;
            }
          }
          else {
            productOrder = new ProductOrders();
            allOrders.push(productOrder);
          }
          if (!productOrder.setting)
            productOrder.setting = new AdvancedReorderConfig();
          if (item?.data?.manualEntry) {
            productOrder.setting.manualEntry = item.data.manualEntry;
            productOrders += item.data.manualEntry;
          }
          if (item?.data?.adjustment) {
            productOrder.setting.adjustment = item.data.adjustment;
            productOrders += productOrders * item.data.adjustment;
          }
          if (productOrders > 0) {
            productOrder.quantity = productOrders;
            order.addedQuantity = Math.ceil(order.addedQuantity + (productOrders * (1 + (order.details.adjustment || 0))));
            sub = new AdvancedReorderSubSetting();
            sub.productId = item.data.productId;
            for (let listing of productOrder.listings) {
              if (listing.quantity > 0) {
                if (listing.storeId) {
                  let listingSetting = new AdvancedReorderListingSetting();
                  listingSetting.productId = item.data.productId;
                  listingSetting.storeId = listing.storeId;
                  listingSetting.asin = listing.asin;
                  listingSetting.quantity = listing.quantity;
                  // this.copyConfig(listing.setting, listingSetting);
                  order.details.listings.push(listingSetting);
                  let sub = order.details.subs.find(s => s.productId == item.data.productId);
                  if (sub) {
                    sub.quantity += listing.quantity;
                  }
                  else {
                    sub = new AdvancedReorderSubSetting();
                    sub.productId = item.data.productId;
                    sub.quantity = listing.quantity;
                    order.details.subs.push(sub);
                  }
                }
                else {
                  sub.quantity = listing.quantity;
                  // this.copyConfig(listing.setting, sub);
                }
              }
            }
            if (Object.keys(sub).length > 1)
              order.details.subs.push(sub);
          }
        }
        this.loadingService.loading = true;
        if (isNew) {
          this.advReorderService.addToOrder(order.productId, order.supplierId, order.addedQuantity, order.details)
            .subscribe(() => {
              if (!item.data.addedQuantity && item.totalReorder)
                item.data.addedQuantity = item.totalReorder;
              else
                delete item.data.addedQuantity;
              this.main.orderDetails = order.details;
              this.main.data.addedQuantity = order.addedQuantity;
              this.addedReorders.push(order);
              this.setTotalReorder();
              for (let item of this.allItems)
                this.refreshOrders(item);
              this.loadingService.loading = false;
          }, err => this.loadingService.loading = false);
        }
        else {
          if (!order.details.subs?.length && order.addedQuantity <= 1)
            order.addedQuantity = 0;
          this.advReorderService.updateOrder(order.productId, order.supplierId, order.addedQuantity, order.details)
            .subscribe(() => {
              if (item.data.addedQuantity || !order.addedQuantity)
                delete item.data.addedQuantity;
              else
                item.data.addedQuantity = item.totalReorder;
              this.main.orderDetails = order.details;
              this.main.data.addedQuantity = order.addedQuantity;
              for (let item of this.allItems)
                this.refreshOrders(item);
              this.setTotalReorder();
              this.loadingService.loading = false;
          }, err => this.loadingService.loading = false);
        }
      }
      this.setTotalReorder();
    } else {
      this.loadingService.loading = true;
      if (item.data.addedQuantity)
      {
        this.advReorderService.removeFromOrder(item.data.productId, item.data.productId,
          item.data.storeId).subscribe(() => {
          delete item.data.addedQuantity;
          this.loadAddedReorders();
        }, err => this.loadingService.loading = false);
      }
      else { 
        let addedQuantity = item.totalReorder;
        this.advReorderService.addToOrder(item.data.productId, item.data.supplierId,
          addedQuantity, item.orderDetails).subscribe(() => {
            item.data.addedQuantity = addedQuantity;
            this.loadAddedReorders();
          }, err => this.loadingService.loading = false);
      }
    }
  }

  loadAddedReorders() {
    this.advReorderService.getAddedReorder().subscribe(res => {
      this.addedReorders = res;
      for (let item of this.allItems)
        this.refreshOrders(item);
      this.loadingService.loading = false;
    }, err => this.loadingService.loading = false);
  }

  setAllOrders(item: AdvancedReorderItem, allOrders: ProductOrders[], outOfStock: boolean, buyBox: boolean) {
    let newAvgOrder = 0, avgCount = 0;
    if (!item.data.hasSubRule) {
      for (let data of item.selected) {
        let periodOrders = 0, days = 0;
        if (data.listings && data.listings.length) {
          for (let dataListing of data.listings) {
            if (dataListing.orders > 0) {
              let productOrders: ProductOrders = allOrders.find(po => po.productId == dataListing.productId);
              if (!productOrders) {
                productOrders = new ProductOrders();
                productOrders.productId = dataListing.productId;
                allOrders.push(productOrders);
              }
              if (!item.data.storeId) {
                productOrders.included = item.data.included;
                if (!productOrders.setting)
                  productOrders.setting = new AdvancedReorderConfig();
                productOrders.setting.adjustment = item.data.adjustment;
                productOrders.setting.manualEntry = item.data.manualEntry;
                productOrders.setting.minusAllStoresInventory = item.formGroup.value.minusStoreInventory == 'true';
                productOrders.setting.minusStores = item.storesFormGroup ? Object.keys(item.storesFormGroup.value).filter(id => item.storesFormGroup.value[id] == true).map(id => Number(id)) : [];      
              }
              if (data.date) {
                if (!item.data.storeId) {
                  productOrders.setting.basedOnHasCustom = true;
                  if (!productOrders.setting.basedOnSelectedMonths)
                    productOrders.setting.basedOnSelectedMonths = [];
                  productOrders.setting.basedOnSelectedMonths.push([data.date.getUTCMonth(), data.date.getUTCFullYear() - 2000]);
                }
                days = moment(data.date).daysInMonth();
              } else {
                if (!item.data.storeId)
                  productOrders.setting.basedOnSelectedDays = data.days
                days = data.days;
              }
              let listing = productOrders.listings.find(l => l.storeId == dataListing.storeId);
              if (dataListing.asin && dataListing.storeId) {
                if (!listing) {
                  listing = new ItemOrders();
                  listing.storeId = dataListing.storeId;
                  productOrders.listings.push(listing);
                }
                listing.asin = dataListing.asin;
              }
              if (item.data.storeId && listing) {
                listing.included = item.data.included;
                if (!listing.setting)
                  listing.setting = new AdvancedReorderConfig();
                listing.setting.adjustment = item.data.adjustment;
                listing.setting.manualEntry = item.data.manualEntry;
                listing.setting.minusAllStoresInventory = item.formGroup.value.minusStoreInventory == 'true';
                listing.setting.minusStores = item.storesFormGroup ? Object.keys(item.storesFormGroup.value).filter(id => item.storesFormGroup.value[id] == true).map(id => Number(id)) : [];
              }
              let percent: number = 1;
              if (outOfStock)
                percent = dataListing.inStock || 1;
              if (buyBox) {
                let buyBoxValue = dataListing.buyBox || 1;
                if (buyBoxValue < percent)
                  percent = buyBoxValue;
              }
              if (dataListing.storeId && listing) {
                listing.quantity += dataListing.orders / percent;
                listing.days += days;
                listing.orderFor = item.formGroup?.value?.totalDays || item.totalOrderFor;
              } else {
                if (!item.data.storeId) {
                  productOrders.quantity += dataListing.orders / percent;
                  productOrders.days += days;
                }
                if (listing)
                  listing.orderFor = item.formGroup?.value?.totalDays || item.totalOrderFor;
              }
              let orders = (dataListing.orders / percent);
              if (orders) {
                if (periodOrders) {
                  if (orders)
                    periodOrders = periodOrders + orders;
                }
                else
                  periodOrders = orders;
              }
            }
          }
        }
        if (periodOrders) {
          newAvgOrder += periodOrders;
          avgCount += data.days;
        }
      }
    }
    item.avgOrders = newAvgOrder / avgCount;
    item.supplyTill = moment().add(((item.data.totalInventory || 0) + (item.totalRealtime || 0)) / item.avgOrders, 'days').toString();
    if (!item.data.hasSubRule) {
      for (let listing of item.data.storeProducts) {
        let productOrders: ProductOrders = allOrders.find(po => po.productId == listing.productId);
        if (!productOrders) {
          productOrders = new ProductOrders();
          productOrders.productId = listing.productId;
          allOrders.push(productOrders);
        }
        let itm = productOrders.listings.find(l => l.storeId == listing.storeId);
        if (!itm) {
          itm = new ItemOrders();
          itm.storeId = listing.storeId;
          if (item.data.hasSubRule) {
            itm.quantity = listing.reorder;
          }
          productOrders.listings.push(itm);
        }
        itm.included = item.data.included && listing.included !== false;
      }
    }
  }

  setAllMainOrders(item: AdvancedReorderItem, allOrders: ProductOrders[], outOfStock: boolean, buyBox: boolean) {
    if (this.main == item) {
      for (let itm of this.allItems) {
        if (itm.data.hasSubRule) {
          let productOrders: ProductOrders = allOrders.find(po => po.productId == itm.data.productId);
          if (!productOrders) {
            productOrders = new ProductOrders();
            productOrders.productId = itm.data.productId;
            allOrders.push(productOrders);
          }
          productOrders.quantity = itm.data.reorderRaw || 0;
          if (!productOrders.setting)
            productOrders.setting = new AdvancedReorderConfig();
          productOrders.setting.adjustment = itm.data.adjustment;
          productOrders.setting.manualEntry = itm.data.manualEntry;
          if (productOrders.quantity && itm.data.adjustment)
            productOrders.quantity *= (1 + itm.data.adjustment);
          if (itm.data.manualEntry)
            productOrders.quantity += itm.data.manualEntry;
          if (productOrders.quantity < 0)
            productOrders.quantity = 0;
        }
        else
          this.setAllOrders(itm, allOrders, outOfStock, buyBox);
      }
    }
    let newAvgOrder = 0, avgCount = 0;
    for (let data of item.selected) {
      let periodOrders = 0, days = 0;
      if (data.listings) {
        for (let dataListing of this.main ? data.listings.filter(l => !this.allItems.some(i =>
          i.data.productId == l.productId && (!i.data.storeId || i.data.storeId == l.storeId))) : data.listings) {
          let productOrders: ProductOrders = allOrders.find(po => po.productId == dataListing.productId);
          if (!productOrders) {
            productOrders = new ProductOrders();
            productOrders.productId = dataListing.productId;
            allOrders.push(productOrders);
          }
          if (!productOrders.setting)
            productOrders.setting = new AdvancedReorderConfig();
          if (data.date) {
            productOrders.setting.basedOnHasCustom = true;
            if (!productOrders.setting.basedOnSelectedMonths)
              productOrders.setting.basedOnSelectedMonths = [];
            productOrders.setting.basedOnSelectedMonths.push([data.date.getUTCMonth(), data.date.getUTCFullYear() - 2000]);
            days = moment(data.date).daysInMonth();
          } else 
            days = productOrders.setting.basedOnSelectedDays = data.days;
          productOrders.setting.minusAllStoresInventory = item.formGroup.value.minusStoreInventory == 'true';
          productOrders.setting.minusStores = item.storesFormGroup ? Object.keys(item.storesFormGroup.value).filter(id => item.storesFormGroup.value[id] == true).map(id => Number(id)) : [];
          if (!item.data.hasSubRule) {
            let listing = productOrders.listings.find(l => l.storeId == dataListing.storeId);
            if ((dataListing.orders > 0 || dataListing.asin) && dataListing.storeId
              && (this.main == item || !this.productId || this.productId == dataListing.productId)) {
              if (!listing) {
                listing = new ItemOrders();
                listing.storeId = dataListing.storeId;
                productOrders.listings.push(listing);
              }
              listing.asin = dataListing.asin;
            }
            if (dataListing.orders > 0) {
              let percent: number = 1;
              if (outOfStock)
                percent = dataListing.inStock || 0;
              if (buyBox) {
                let buyBoxValue = dataListing.buyBox || 0;
                if (buyBoxValue < percent)
                  percent = buyBoxValue;
              }
              if (dataListing.storeId && listing) {
                if (this.main == item || !this.productId || this.productId == dataListing.productId) {
                  listing.quantity += dataListing.orders / (percent || 1);
                  listing.days += days;
                  listing.orderFor = item.totalOrderFor;
                }
              } else {
                productOrders.quantity += dataListing.orders / (percent || 1);
                productOrders.days += days;
                productOrders.orderFor = item.totalOrderFor;
              }
              let orders = (dataListing.orders / (percent || 1));
              if (orders) {
                if (periodOrders) {
                  if (orders)
                    periodOrders = periodOrders + orders;
                }
                else
                  periodOrders = orders;
              }
            }
          }
        }
      }
      if (periodOrders) {
        newAvgOrder += periodOrders;
        avgCount += data.days;
      }
    }
    item.avgOrders = newAvgOrder / avgCount;
    item.supplyTill = moment().add(((item.data.totalInventory || 0) + (item.totalRealtime || 0)) / item.avgOrders, 'days').toString();
    if (this.main == item) {
      for (let sp of item.data.storeProducts) {
        if (this.productId && this.productId == sp.productId && !sp.storeId)
          this.subIncluded = sp.included;
        let productOrders: ProductOrders = allOrders.find(po => po.productId == sp.productId);
        if (!productOrders) {
          productOrders = new ProductOrders();
          productOrders.productId = sp.productId;
          allOrders.push(productOrders);
        }
        if (!productOrders.setting)
          productOrders.setting = new AdvancedReorderConfig();
        if (sp.included === false) {
          productOrders.quantity = 0;
          for (let listing of productOrders.listings)
            listing.quantity = 0;
        } else if (sp.storeId) {
          if (!item.data.hasSubRule && (this.main == item || !this.productId || this.productId == sp.productId)) {
            let itm = productOrders.listings.find(l => l.storeId == sp.storeId);
            if (!itm) {
              itm = new ItemOrders();
              itm.storeId = sp.storeId;
              itm.quantity = sp.reorder || 0;
              itm.setting = new AdvancedReorderConfig();
              itm.setting.adjustment = sp.adjustment;
              itm.setting.manualEntry = sp.manualEntry;
              productOrders.listings.push(itm);
            }
          }
        } else if (!productOrders.quantity) {
          productOrders.quantity = sp.reorder;
          if (!productOrders.setting.adjustment)
            productOrders.setting.adjustment = sp.adjustment;
          if (!productOrders.setting.manualEntry)
            productOrders.setting.manualEntry = sp.manualEntry;
        }
      }
    }
  }

  totalOrders(item: AdvancedReorderItem, outOfStock: boolean = item.formGroup.value.outOfStock,
    buyBox: boolean = item.formGroup.value.buyBox, days: number = undefined, setDatails: boolean = false) {
    if (days === 0)
      return 0;
    let allOrders: ProductOrders[] = [];
    if (item?.selected) {
      if (this.main)
        this.setAllOrders(item, allOrders, outOfStock, buyBox);
      else
        this.setAllMainOrders(item, allOrders, outOfStock, buyBox);
    }
    let totalOrders = 0, overstockedInventory = 0, totalRealtime = 0;
    let productOrders: ProductOrders = allOrders.find(po => po.productId == item.data.productId);
    if (!productOrders) {
      productOrders = new ProductOrders();
      productOrders.productId = item.data.productId;
      allOrders.push(productOrders);
    }
    item.totalRealtime = 0;
    if (setDatails || !item.overstockedStores) {
      item.overstockedStores = new Map<number, number>();
      item.storeUsedRealtime = new Map<number, number>();
    }
    allOrders.forEach((prodOrders) => {
      let productOrders = 0;
      let totalOrderFor: number = item.formGroup?.value?.totalDays || item.totalOrderFor;
        if (prodOrders.listings.length) {
          prodOrders.listings.forEach((listing) => {
            if (listing.included !== false) {
              let orders: number;
              if (listing.days)
                orders = (listing.quantity / listing.days) * (listing.orderFor || totalOrderFor);
              else
                orders = listing.quantity;
              let realTime = item.data.storeProducts.find(sp => sp.storeId == listing.storeId && sp.productId == prodOrders.productId)?.realTime || 0;
              if (days !== undefined && totalOrderFor) {
                if (listing.setting ? (listing.setting.minusAllStoresInventory || listing.setting.minusStores?.includes(listing.storeId)) :
                  (prodOrders.setting ? (prodOrders.setting.minusAllStoresInventory || prodOrders.setting.minusStores?.includes(listing.storeId)) :
                  item.minusAllStoresInventory || (item.storesFormGroup ? item.storesFormGroup.value[listing.storeId] :
                    item.data.minusStores.includes(listing.storeId)))) {
                  if (realTime > orders) {
                    if (setDatails) {
                      overstockedInventory += realTime - orders;
                      item.overstockedStores.set(listing.storeId, (item.overstockedStores.get(listing.storeId) || 0) + realTime - orders);
                      totalRealtime += orders;
                      item.storeUsedRealtime.set(listing.storeId, (item.storeUsedRealtime.get(listing.storeId) || 0) + orders);
                    }
                    orders = 0;
                  } else {
                    orders -= realTime;
                    if (setDatails) {
                      totalRealtime += realTime;
                      item.storeUsedRealtime.set(listing.storeId, (item.storeUsedRealtime.get(listing.storeId) || 0) + realTime);
                    }
                  }
                }
                orders = (orders / totalOrderFor) * days;
              }
              if (orders && listing.setting?.adjustment)
                orders *= 1 + listing.setting.adjustment;
              if (listing.setting?.manualEntry)
                orders += listing.setting.manualEntry;
              listing.quantity = orders;
              productOrders += orders;
              item.totalRealtime += realTime;
            }
          });
        } else
          productOrders = prodOrders.quantity;
        prodOrders.quantity = productOrders;
        if (this.productId && this.productId == prodOrders.productId)
          this.subReorder = Math.ceil((productOrders * (prodOrders.setting?.adjustment ?
            (1 + prodOrders.setting?.adjustment) : 1)) + (prodOrders.setting?.manualEntry || 0));
        if (prodOrders.listings.length)
          totalOrders += (productOrders * (prodOrders.setting?.adjustment ?
            (1 + prodOrders.setting?.adjustment) : 1)) + (prodOrders.setting?.manualEntry || 0);
        else
          totalOrders += productOrders;

    });
    if (setDatails) {
      item.overstockedStoreInventory = overstockedInventory;
      item.usedRealtime = totalRealtime;
      item.orderDetails = new AdvancedReorderSetting();
      if (item.data.adjustment)
        item.orderDetails.adjustment = item.data.adjustment;
      if (item.data.manualEntry)
        item.orderDetails.manualEntry = item.data.manualEntry;
      item.orderDetails.minusPOs = item.data.minusOpenPurchases;
      item.orderDetails.minusAllInventory = item.minusAllInventory;
      if (!item.minusAllInventory && item.data.minusInventory)
        item.orderDetails.minusLocations = item.data.minusInventory.split(',').map(id => Number(id));
      item.orderDetails.minusAllStoresInventory = item.minusAllStoresInventory;
      if (!item.minusAllStoresInventory && item.data.minusStoreInventory)
        item.orderDetails.minusStores = item.data.minusStoreInventory.split(',').map(id => Number(id));
      item.orderDetails.minusAllStoresOverstockedInventory = item.minusAllStoresOverstockedInventory;
      if (!item.minusAllStoresOverstockedInventory && item.data.minusOverstockedStoreInventory)
        item.orderDetails.minusOverstockedStores = item.data.minusOverstockedStoreInventory.split(',').map(id => Number(id));
      for (let prodOrders of allOrders) {
        let sub: any = { productId: prodOrders.productId };
        for (let listing of prodOrders.listings) {
          if (listing.quantity > 0) {
            let listingSetting = new AdvancedReorderListingSetting();
            listingSetting.productId = prodOrders.productId;
            listingSetting.storeId = listing.storeId;
            listingSetting.asin = listing.asin;
            listingSetting.quantity = listing.quantity;
            item.orderDetails.listings.push(listingSetting);
          }
        }
        if (prodOrders.quantity > 0)
          sub.quantity = prodOrders.quantity;
        if (Object.keys(sub).length > 1)
          item.orderDetails.subs.push(sub);
      }
    }
    return totalOrders || 0;
  }

  totalUnits(item: AdvancedReorderItem) {
    let totalUnits = 0;
    if (item?.selected) {
      for (let sub of item.selected) {
        if (sub.listings) {
          for (let listing of sub.listings)
            totalUnits += listing.orders || 0;
        }
        else
          totalUnits += Number(sub.orders) || 0;
      }
    }
    return totalUnits || 0;
  }

  calculateAdjustment(item: AdvancedReorderItem) : number {
    let reorder = 0;
    if (item.data.hasSubRule)
      reorder = item.data.reorder;
    else
      reorder = item.totalReorder;
    if (item.data.manualEntry) {
      reorder -= item.data.manualEntry;
      if (reorder < 0)
        reorder = 0;
    }
    if (reorder && item.data.adjustment) {
      return reorder - (reorder / (1 + item.data.adjustment));
    }
    return 0;
  }

  avgInStock(item: AdvancedReorderItem) {
    return (item.selected || []).reduce((a, b) => a + (b.listings?.reduce((c, d) => c + (d.inStock ? (d.orders || 0) : 0), 0) || 0), 0) / ((item.selected || []).reduce((a, b) => a + (b.listings?.reduce((c, d) => c + (d.inStock ? (d.orders / d.inStock || 0) : 0), 0) || 0), 0) || 1);
  }

  avgBuyBox(item: AdvancedReorderItem) {
    return (item.selected || []).reduce((a, b) => a + (b.listings?.reduce((c, d) => c + (d.buyBox ? (d.orders || 0) : 0), 0) || 0), 0) / ((item.selected || []).reduce((a, b) => a + (b.listings?.reduce((c, d) => c + (d.buyBox ? (d.orders / d.buyBox || 0) : 0), 0) || 0), 0) || 1);
  }

  get vendors() {
    let vendors = new Set();
    this.addedReorders.forEach(i => vendors.add(i.supplierId));
    return vendors.size;
  }

  get totalCost() {
    let total = 0;
    this.addedReorders.forEach(item => total += ((item.addedQuantity * (
      item.allPrices.find(p => p.supplierId == item.supplierId) ||
        (item.allPrices && item.allPrices.length ? item.allPrices[0] : {})).price) || 0));
    return total;
  }
  
  setOverstockOption(item: AdvancedReorderItem) {
    if (!this.isOverstockedDisabled(item))
      item.formGroup.controls.minusOverstockedStoreInventory.setValue(!item.formGroup.value.minusOverstockedStoreInventory)
  }

  setOverstockStore(item: AdvancedReorderItem, storeId: number) {
    if (item.storesFormGroup.value[storeId]) {
      item.overstockedStoresFormGroup.controls[storeId].setValue(!item.overstockedStoresFormGroup.value[storeId]);
    }
  }

  allSelected(formGroup: UntypedFormGroup) {
    for (let item of Object.keys(formGroup.value)) {
      if (!formGroup.value[item])
        return false;
    }
    return true;
  }

  includeAllLocations(e, item) {
    e.target.checked = !item.formGroup.value.minusInventory;
    item.formGroup.controls.minusInventory.setValue(!item.formGroup.value.minusInventory);
  }

  includeAllStores(e, item) {
    e.target.checked = !item.formGroup.value.minusStoreInventory;
    item.formGroup.controls.minusStoreInventory.setValue(!item.formGroup.value.minusStoreInventory);
  }

  includeAllOverstockedStores(e, item) {
    e.target.checked = !item.formGroup.value.minusOverstockedStoreInventory;
    if (!this.isOverstockedDisabled(item))
      item.formGroup.controls.minusOverstockedStoreInventory.setValue(!item.formGroup.value.minusOverstockedStoreInventory);
    else
      e.target.checked = false;
  }

  resetItemConfigs(item: AdvancedReorderItem, itemConfigs: AdvancedReorderConfig = this.itemConfigs) {
    if (!itemConfigs)
      return;
    if (itemConfigs.ruleId)
      item.formGroup.controls.reorderRuleId.setValue(itemConfigs.ruleId);
    else {
      item.formGroup.controls.adjustment.setValue(itemConfigs.adjustment);
      item.adjSign = itemConfigs.adjustment < 0 ? '-' : '+';
      item.formGroup.controls.outOfStock.setValue(itemConfigs.outOfStock);
      item.formGroup.controls.buyBox.setValue(itemConfigs.buyBox);
      item.formGroup.controls.minusInventory.setValue(false);
      item.formGroup.controls.minusInventory.setValue(itemConfigs.minusAllInventory);
      item.formGroup.controls.minusStoreInventory.setValue(false);
      item.formGroup.controls.minusStoreInventory.setValue(itemConfigs.minusAllStoresInventory);
      item.formGroup.controls.minusOverstockedStoreInventory.setValue(false);
      item.formGroup.controls.minusOverstockedStoreInventory.setValue(itemConfigs.minusAllStoresOverstockedInventory);
      for (let id of Object.keys(item.locationsFormGroup.value))
        item.locationsFormGroup.controls[id].setValue(itemConfigs.minusLocations.includes(id));
      for (let id of Object.keys(item.storesFormGroup.value))
        item.storesFormGroup.controls[id].setValue(itemConfigs.minusStores.includes(id));
      item.formGroup.controls.minusPOs.setValue(itemConfigs.minusPOs);
      item.formGroup.controls.manualEntry.setValue(itemConfigs.manualEntry);
    }
    if (this.itemConfigs == itemConfigs)
      delete this.itemConfigs;
  }

  getItemConfigs(item: AdvancedReorderItem) : AdvancedReorderConfig {
    let itemConfigs = new AdvancedReorderConfig();
    itemConfigs.applyToAll = this.applyToAll;
    if (item.formGroup.value.reorderRuleId)
      itemConfigs.ruleId = item.formGroup.value.reorderRuleId
    else {
      itemConfigs.adjustment = item.formGroup.value.adjustment;
      itemConfigs.outOfStock = item.formGroup.value.outOfStock;
      itemConfigs.buyBox = item.formGroup.value.buyBox;
      itemConfigs.minusAllInventory = item.minusAllInventory;
      itemConfigs.minusAllStoresInventory = item.minusAllStoresInventory;
      itemConfigs.minusAllStoresOverstockedInventory = item.minusAllStoresOverstockedInventory;
      itemConfigs.minusLocations = Object.keys(item.locationsFormGroup.value)
        .filter(id => item.locationsFormGroup.value[id] == true);
      itemConfigs.minusStores = Object.keys(item.storesFormGroup.value)
        .filter(id => item.storesFormGroup.value[id] == true);
      itemConfigs.minusPOs = item.formGroup.value.minusPOs;
      itemConfigs.manualEntry = item.data.manualEntry;
      item.latest.forEach((orders) => {
        if (orders.selected) {
          itemConfigs.basedOnSelectedDays = orders.days;
          return;
        }
      });
      item.years.forEach((months, year) => {
        for (let month of months) {
          if (month.selected) {
            itemConfigs.basedOnHasCustom = true;
            if (!itemConfigs.basedOnSelectedMonths)
              itemConfigs.basedOnSelectedMonths = [];
            itemConfigs.basedOnSelectedMonths.push([month.date.getUTCMonth(), month.date.getUTCFullYear() - 2000])
          }
        }
      });
      item.orderForYears.forEach((months, year) => {
        for (let month of months) {
          if (month.selected) {
            if (!itemConfigs.orderForHasCustom)
              itemConfigs.orderForSelectedMonths = [];
            itemConfigs.orderForHasCustom = true;
            itemConfigs.orderForSelectedMonths.push([month.date.getUTCMonth(), month.date.getUTCFullYear() - 2000])
          }
        }
      });
      if (!itemConfigs.orderForHasCustom)
        itemConfigs.orderForSelectedDays = item.data.orderFor;
    }
    return itemConfigs;
  }

  modifyCal(item: AdvancedReorderItem) {
    this.itemConfigs = this.getItemConfigs(item);
    item.focus = true;
    item.showLocation = false
  }

  closeCal(item: AdvancedReorderItem, cancel: boolean = false) {
    item.focus = false;
    if (cancel)
      this.resetItemConfigs(item);
  }

  sliderMouseDown(e: AdvancedReorderSliderComponent) {
    this.activeSlider = e;
  }

  setTotalReorder(item: AdvancedReorderItem = this.main, loadMain: boolean = true) {
    if (this.main && item == this.main) {
      var result = ((this.mainTotalOrders(this.main.formGroup.value.outOfStock,
        this.main.formGroup.value.buyBox, this.main.data.orderFor || this.main.totalOrderFor,
        true) - (this.main.formGroup.value.minusInventory ? this.inventory(this.main) : 0)
        - (this.main.formGroup.value.minusPOs ? this.main.data.openPurchaseOrders || 0 : 0))
        * (this.main.data.adjustment ? (1 + this.main.data.adjustment) : 1)) + (this.main.data.manualEntry || 0);;
      this.main.totalReorder = result >= 0 ? Math.ceil(result) : 0;
      for (let item of this.allItems)
        this.setTotalReorder(item, false);
    } else {
      if (!item.data.hasSubRule) {
        var result = (((this.totalOrders(item, item.formGroup.value.outOfStock, item.formGroup.value.buyBox,
          (item.formGroup.value.totalDays || item.data.orderFor || item.totalOrderFor), true))
          - (!this.main ? (item.formGroup.value.minusInventory ? this.inventory(item) : 0)
          - (item.formGroup.value.minusPOs ? item.data.openPurchaseOrders || 0 : 0)
          - (item.formGroup.value.minusOverstockedStoreInventory ? item.overstockedStoreInventory || 0 : 0) : 0))
        * (item.data.adjustment ? (1 + item.data.adjustment) : 1)) + (item.data.manualEntry || 0);
        item.totalReorder = result >= 0 ? Math.ceil(result) : 0;
      } else {
        item.totalReorder = item.data.reorderRaw || 0;
        if (!this.main) {
          item.totalReorder -= (item.formGroup.value.minusInventory ? this.inventory(item) : 0);
          if (item.totalReorder < 0)
            item.totalReorder = 0;
        }
        if (item.totalReorder && item.data.adjustment)
          item.totalReorder *= (1 + item.data.adjustment);
        if (item.data.manualEntry)
          item.totalReorder += item.data.manualEntry;
        if (item.totalReorder < 0)
          item.totalReorder = 0;
        else
          item.totalReorder = Math.ceil(item.totalReorder);
      }
      if (this.main && loadMain)
        this.setTotalReorder(this.main, false);
    }
  }

  createSettings(item: AdvancedReorderItem) : AdvancedReorderSettingModel {
    let setting = new AdvancedReorderSettingModel();
    setting.productId = item.data.productId;
    setting.storeId = item.data.storeId;
    setting.basedOn = item.data.basedOn;
    setting.basedOnMonths = item.data.basedOnMonths;
    setting.orderFor = item.data.orderFor;
    setting.orderForMonths = item.data.orderForMonths;
    setting.outOfStock = item.formGroup.value.outOfStock;
    setting.buyBox = item.formGroup.value.buyBox;
    setting.minusInventory = item.minusAllInventory ? 'true' : Object.keys(item.locationsFormGroup.value)
      .map(id => Number(id)).filter(id => item.locationsFormGroup.value[id] == true).join(';');
    setting.minusOpenPurchases = item.formGroup.value.minusPOs;
    setting.minusStoreInventory = item.minusAllStoresInventory ? 'true' : Object.keys(item.storesFormGroup.value)
      .map(id => Number(id)).filter(id => item.storesFormGroup.value[id] == true).join(';');
    setting.minusStoreInventory = item.formGroup.value.minusStoreInventory;
    setting.minusOverstockedStoreInventory = item.minusAllStoresOverstockedInventory ? 'true' :
      Object.keys(item.overstockedStoresFormGroup.value).map(id => Number(id))
      .filter(id => item.overstockedStoresFormGroup.value[id] == true).join(';');
    setting.adjustment = item.formGroup.value.adjustment * (item.adjSign == '-' ? -1 : 1);
    setting.manualEntry = item.data.manualEntry;
    setting.subOnly = !!this.main;
    setting.included = item.data.included;
    return setting;
  }

  saveItemSettings(item: AdvancedReorderItem) {
    this.loadingService.loading = true;
    this.advReorderService.addSetting(this.createSettings(item)).subscribe(() => {
      this.loadingService.loading = false;
      item.unsavedChanges = false;
      item.hasCustomSettings = true;
      if (this.allItems.every(item => !item.unsavedChanges))
        this.unsavedChanges = false;
      this.settingChanged = true;
    }, err => this.loadingService.loading = false);
    this.modalService.dismissAll();
  }

  saveSettings() {
    let settings: AdvancedReorderSettingModel[] = [];
    for (let item of this.allItems) {
      if (item.unsavedChanges) {
        settings.push(this.createSettings(item));
        item.hasCustomSettings = true;
        item.unsavedChanges = false;
      }
    }
    if (settings.length) {
      this.loadingService.loading = true;
      this.advReorderService.saveSettings(settings).subscribe(() => this.loadingService.loading = false,
        err => this.loadingService.loading = false);
    }
    this.unsavedChanges = false;
    this.settingChanged = true;
  }

  removeItemSettings(item: AdvancedReorderItem) {
    this.loadingService.loading = true;
    this.advReorderService.deleteSetting(item.data.productId, item.data.storeId, !!this.main).subscribe(() => {
      this.loadingService.loading = false;
      item.data.included = true;
      item.unsavedChanges = false;
      item.hasCustomSettings = false;
      this.resetItemConfigs(item, this.configs);
      if (this.allItems.every(item => !item.unsavedChanges))
        this.unsavedChanges = false;
      this.settingChanged = true;
    }, err => this.loadingService.loading = false);
  }

  toggleInclude(item: AdvancedReorderItem) {
    item.data.included = !item.data.included;
    if (this.main && this.main != item && !this.productId)
      this.main.data.storeProducts.find(sp => sp.productId == item.data.productId && !sp.storeId).included = item.data.included;
    if (item.data.storeId && item.data.storeProducts.length)
      item.data.storeProducts[0].included = item.data.included;
    if (item.data.included)
      this.removeItemSettings(item);
    else
      this.saveItemSettings(item);
    this.refreshAdjustment(item);
  }

  mainTotalOrders(outOfStock: boolean = this.formGroup.value.outOfStock, buyBox: boolean = this.formGroup.value.buyBox,
    days: number = undefined, setDetails: boolean = false) {
    if (days === 0)
      return 0;
    let allOrders: ProductOrders[] = [];
    if (this.main?.selected)
      this.setAllMainOrders(this.main, allOrders, outOfStock, buyBox);
    let totalOrders = 0, overstockedInventory = 0, totalRealtime = 0;
    let productOrders: ProductOrders = allOrders.find(po => po.productId == this.main.data.productId);
    if (!productOrders) {
      productOrders = new ProductOrders();
      productOrders.productId = this.main.data.productId;
      allOrders.push(productOrders);
    }
    this.main.totalRealtime = 0;
    if (setDetails) {
      this.main.overstockedStores = new Map<number, number>();
      this.main.storeUsedRealtime = new Map<number, number>();
    }
    allOrders.forEach((prodOrders) => {
      if (prodOrders.included !== false) {
        let productOrders = 0;
        let totalOrderFor: number = this.main.formGroup?.value?.totalDays || this.main.totalOrderFor;
        if (prodOrders.listings.length) {
          prodOrders.listings.forEach((listing) => {
            if (listing.included !== false) {
              let orders: number;
              if (listing.days)
                orders = (listing.quantity / listing.days) * (listing.orderFor || totalOrderFor);
              else
                orders = listing.quantity;
              let realTime = this.main.data.storeProducts.find(sp => sp.storeId == listing.storeId && sp.productId == prodOrders.productId)?.realTime || 0;
              if (days !== undefined && totalOrderFor) {
                if (listing.setting ? (listing.setting.minusAllStoresInventory || listing.setting.minusStores?.includes(listing.storeId)) :
                  (prodOrders.setting ? (prodOrders.setting.minusAllStoresInventory || prodOrders.setting.minusStores?.includes(listing.storeId)) :
                  this.main.minusAllStoresInventory || (this.main.storesFormGroup ? this.main.storesFormGroup.value[listing.storeId] :
                    this.main.data.minusStores.includes(listing.storeId)))) {
                  if (realTime > orders) {
                    if (setDetails) {
                      overstockedInventory += realTime - orders;
                      this.main.overstockedStores.set(listing.storeId, (this.main.overstockedStores.get(listing.storeId) || 0) + realTime - orders);
                      totalRealtime += orders;
                      this.main.storeUsedRealtime.set(listing.storeId, (this.main.storeUsedRealtime.get(listing.storeId) || 0) + orders);
                    }
                    orders = 0;
                  } else {
                    orders -= realTime;
                    if (setDetails) {
                      totalRealtime += realTime;
                      this.main.storeUsedRealtime.set(listing.storeId, (this.main.storeUsedRealtime.get(listing.storeId) || 0) + realTime);
                    }
                  }
                }
                orders = (orders / totalOrderFor) * days;
              }
              if (orders && listing.setting?.adjustment)
                orders *= 1 + listing.setting.adjustment;
              if (listing.setting?.manualEntry)
                orders += listing.setting.manualEntry;
              listing.quantity = orders;
              productOrders += orders;
              this.main.totalRealtime += realTime;
            }
          });
        } else
          productOrders = prodOrders.quantity;
        prodOrders.quantity = productOrders;
        if (this.productId && this.productId == prodOrders.productId)
          this.subReorder = Math.ceil((productOrders * (prodOrders.setting?.adjustment ?
            (1 + prodOrders.setting?.adjustment) : 1)) + (prodOrders.setting?.manualEntry || 0));
        if (prodOrders.listings.length)
          totalOrders += (productOrders * (prodOrders.setting?.adjustment ?
            (1 + prodOrders.setting?.adjustment) : 1)) + (prodOrders.setting?.manualEntry || 0);
        else
          totalOrders += productOrders;
      }
    });
    return totalOrders;
  }

  getTotalAdjustment(item: AdvancedReorderItem) {
    return item.data.storeProducts.reduce((a, b) => a + (b.adjustment ?
      ((b.reorder - (b.manualEntry || 0))/(1 + b.adjustment)) * b.adjustment : 0), 0) || 0;
  }

  getTotalManualEntry(item: AdvancedReorderItem) {
    return item.data.storeProducts.reduce((a, b) => a + (b.manualEntry || 0), 0) || 0;
  }

  getRuleName(advReorderRuleId: number) {
    return this.advReorderRules.getAll()?.find(r => r.id == advReorderRuleId)?.name;
  }

  cloneItem(item: AdvancedReorderItem) {
    let newItem = { ...item };
    delete newItem.formGroup;
    delete newItem.locationsFormGroup;
    delete newItem.storesFormGroup;
    delete newItem.overstockedStoresFormGroup;
    return newItem;
  }

  unlock(item: AdvancedReorderItem) {
    this.modalService.open(HasSubSettingsComponent).result.then(res => {
      if (res)
        item.data.hasSubRule = false;
    });
  }
}